import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import { Line } from 'ui/icons';
import { body2, body2Bold, caption, colors, heading3, spacing } from 'ui/theme';
import { HEIGHT_OF_CONVERSION_CONTENT } from '../constants';
import { getMultiConversionSymbol, getTopShapePosition } from '../utils';
import { CONVERSION_SHAPE_SIZES, LocalConversionEventStateType, ShapeTypes } from '../types';

interface Props {
  conversions: LocalConversionEventStateType[];
  conversionRateLabel: string;
  conversionRateDividend: number;
  toggleConversionSelection: (key: string) => void;
}

type MetricItemProps = Omit<
  LocalConversionEventStateType,
  'histogram' | 'maxEventValueOverTime'
> & {
  conversionRateLabel: string;
  conversionRateDividend: number;
};

type MetricItemContainerProps = {
  borderColor: string;
  active: boolean;
};

type MetricItemShapeProp = {
  shape: ShapeTypes;
  color: string;
};

const MetricListContainer = styled.ul`
  display: grid;
  grid-template-rows: repeat(5, min-content);
  grid-template-columns: repeat(1, 1fr);
  grid-row-gap: ${spacing.base};
  grid-column-gap: ${spacing.base};
  padding: 0 ${spacing.half} ${spacing.half} 0;
  margin: 0;
  height: ${pxToRem(HEIGHT_OF_CONVERSION_CONTENT)};
  overflow-y: auto;
`;

const MetricItemContainer = styled.li<MetricItemContainerProps>`
  display: grid;
  grid-template-areas:
    'Metric-Item-Shape Metric-Item-Label Metric-Converson-rate'
    'Padding Metric-Item-Value  Metric-Converson-rate';
  grid-template-columns: 0.1fr 1fr 1fr;
  padding: ${spacing.half} ${spacing.threeQuarter};
  border-radius: ${spacing.quarter};
  border: ${({ borderColor, active }) =>
    `${spacing.single} solid ${active ? borderColor : 'transparent'}`};
  background: ${({ active }) => (active ? colors.white : colors.backgroundSecondary)};
  cursor: pointer;
  &:hover {
    border-width: ${spacing.eighth};
    margin: -${spacing.single};
    background: ${({ active }) => (active ? colors.white : colors.grey5)};
  }
`;

const MetricItemLabel = styled.div`
  ${body2};
  color: ${colors.secondaryText};
  grid-area: Metric-Item-Label;
`;

const MetricItemValue = styled.div`
  ${heading3};
  color: #000;
  grid-area: Metric-Item-Value;
`;

const MetricItemShape = styled.div<MetricItemShapeProp>`
  grid-area: Metric-Item-Shape;
  align-self: baseline;
  position: relative;
  top: ${({ shape }) => pxToRem(getTopShapePosition(shape))};
  ${({ shape, color }) => `
    &::after {
      content: '${getMultiConversionSymbol(shape)}';
      font-size: ${CONVERSION_SHAPE_SIZES[shape]};
      color: ${color}
    }
  `}
`;

const MetricConversionRate = styled.div`
  display: grid;
  grid-template-columns: 0.2fr 1fr;
  grid-auto-flow: column;
  align-items: baseline;
  justify-items: center;
  margin: ${spacing.threeEights} 0;
  padding: ${spacing.quarter} 0;
  border-radius: ${spacing.quarter};
  background-color: ${colors.grey1};
  grid-area: Metric-Converson-rate;
`;

const RateText = styled.p`
  ${caption};
  strong {
    ${body2Bold}
  }
`;

const MetricItem = ({
  eventName,
  eventValue,
  color,
  selected,
  shape,
  conversionRateLabel,
  conversionRateDividend,
}: MetricItemProps) => {
  return (
    <MetricItemContainer
      id={eventName}
      borderColor={color}
      active={selected}
      data-testid="conversion-metric-item"
      data-selected={selected}
    >
      <MetricItemShape color={color} shape={shape} />
      <MetricItemLabel>{eventName}</MetricItemLabel>
      <MetricItemValue>{eventValue}</MetricItemValue>
      <MetricConversionRate data-testid="conversion-metric-item-rate">
        <Line width={14} height={6} />
        <RateText>
          <strong>{(eventValue / conversionRateDividend).toFixed(eventValue > 0 ? 2 : 0)}</strong>
          &nbsp;{conversionRateLabel}
        </RateText>
      </MetricConversionRate>
    </MetricItemContainer>
  );
};

export const ConversionMetricList = ({
  conversions,
  toggleConversionSelection,
  conversionRateLabel,
  conversionRateDividend,
}: Props) => (
  <MetricListContainer
    onClick={(event) => {
      const elem = event.target as HTMLInputElement;
      const id = elem.closest('li')?.id;
      if (id) {
        toggleConversionSelection(id);
      }
    }}
  >
    {conversions.map(({ eventName, eventValue, color, selected, shape }) => (
      <MetricItem
        key={eventName}
        eventName={eventName}
        eventValue={eventValue}
        color={color}
        selected={selected}
        shape={shape}
        conversionRateLabel={conversionRateLabel}
        conversionRateDividend={conversionRateDividend}
      />
    ))}
  </MetricListContainer>
);
